import { NEW_LOOK_RADIUS } from "common/NewLook/tokens.ts";
import SignalsGroupsSection from "features/onboarding/methodSettings/signalsGroup/groupCards/SiganlsGroupsSection";
import React, { ReactNode, useContext, useState } from "react";
import GroupsPageProvider from "@shared/features/groups/GroupsPageProvider";
import { GroupsPageContext } from "@shared/features/groups/GroupsPage.context";
import styled, { useTheme } from "styled-components";
import MissingGroupButton from "features/configuration/AccountsAndGroups/MissingGroupButton";
import PageTabs, { TabItem } from "common/Tabs/PageTabs.tsx";
import { SignalsGroupsType } from "@shared/features/onboarding/types.ts";
import { useBottomNavigator, useDisablePageScroll } from "utils/hooks/misc.tsx";
import usePageWrapper from "common/Home/PageWrapper/usePageWrapper.ts";
import { useUser } from "@shared/utils/hooks/misc.ts";
import { typography } from "@shared/common/Themes/typography.ts";
import CreatePersonalGroupButton from "features/bots/Groups/CreatePersonalGroupButton.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { CONNECT_WHOP_CHANNEL_PATH, IS_WHOP } from "common/constants.ts";
import { selectWorkspace } from "@shared/slices/common.slice.ts";
import { useSelector } from "react-redux";
import { WorkspaceType } from "@shared/types";
import { useHistory } from "react-router-dom";
import Button from "common/Buttons/Button";
import PlusOutlined from "@ant-design/icons/PlusOutlined";
import { NEW_LOOK_PAGE_GUTTER_VAR } from "common/NewLook/layout";
import { usePageIntro } from "common/NewLook";
import NewLookIntroTabs from "common/Tabs/NewLookIntroTabs";

const GroupsPage = () => {
  return (
    <GroupsPageProvider isGroupsPage={true}>
      <GroupsPageContent />
    </GroupsPageProvider>
  );
};

export default GroupsPage;

const GroupsPageContent = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.GROUPS);
  const theme = useTheme();
  const history = useHistory();
  const {
    myGroupsData,
    freeTrialGroupsData,
    personalGroupsData,
    noExternalAccountConnected,
  } = useContext(GroupsPageContext)!;
  const { user } = useUser();
  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  useDisablePageScroll(isBottomNavigator);
  usePageWrapper({
    showSearch: true,
  });
  const workspace = useSelector(selectWorkspace);
  const isAdminWorkspace = workspace === WorkspaceType.ADMIN;
  const basePadding = isBottomNavigator ? 16 : 20;
  const isAssetManager = user?.isAssetManager();
  const showPersonalGroups =
    isAssetManager || !!personalGroupsData.groups?.length;

  const isNewLookPage = theme.isNewLook && !isBottomNavigator;
  // New look (desktop): the sections become page tabs, so the section title
  // is the selected tab and only the section's controls stay in its header.
  const isNewLookTabs = isNewLookPage && !IS_WHOP;
  const sectionProps = {
    style: { height: isBottomNavigator ? "100%" : "auto" },
    titleStyle: isNewLookTabs
      ? NEW_LOOK_HIDDEN_TITLE
      : isNewLookPage
      ? {
          color: theme.colors.titleColor,
          fontSize: 16,
          fontWeight: theme.typography.fontWeights.SEMI_BOLD,
        }
      : { color: theme.colors.secondaryGrey },
    showSearch: true,
  };

  const personalGroupsSection = showPersonalGroups ? (
    <SignalsGroupsSection
      {...personalGroupsData}
      {...sectionProps}
      extraButton={<CreatePersonalGroupButton style={{ marginLeft: 8 }} />}
    />
  ) : undefined;

  const getMyGroupsExtraButton = () => {
    if (IS_WHOP) {
      return isAdminWorkspace ? (
        <Button
          onClick={() => history.push(CONNECT_WHOP_CHANNEL_PATH)}
          style={{ marginLeft: 8 }}
        >
          <PlusOutlined />
          Add Group
        </Button>
      ) : undefined;
    } else if (!noExternalAccountConnected) {
      return (
        <MissingGroupButton
          style={{ marginLeft: 8, fontSize: typography.fontSizes.M }}
        />
      );
    }
  };

  const myGroupsSection = (
    <SignalsGroupsSection
      {...myGroupsData}
      {...sectionProps}
      extraButton={getMyGroupsExtraButton()}
    />
  );

  const freeTrialGroupsSection = (
    <SignalsGroupsSection {...freeTrialGroupsData} {...sectionProps} />
  );

  const tabsItems: TabItem[] = [
    {
      key: SignalsGroupsType.MY_GROUPS,
      label: t(SignalsGroupsType.MY_GROUPS),
      children: myGroupsSection,
    },
  ];

  if (!IS_WHOP) {
    tabsItems.push({
      key: SignalsGroupsType.FREE_TRIAL,
      label: t(SignalsGroupsType.FREE_TRIAL),
      children: freeTrialGroupsSection,
    });
  }

  if (showPersonalGroups) {
    tabsItems.push({
      key: SignalsGroupsType.PERSONAL,
      label: t(SignalsGroupsType.PERSONAL),
      children: personalGroupsSection,
    });
  }

  if (isBottomNavigator && !IS_WHOP) {
    return <PageTabs items={tabsItems} />;
  }

  if (isNewLookTabs) {
    const counts: Record<string, number | undefined> = {
      [SignalsGroupsType.MY_GROUPS]: myGroupsData.groups?.length,
      [SignalsGroupsType.FREE_TRIAL]: freeTrialGroupsData.groups?.length,
      [SignalsGroupsType.PERSONAL]: personalGroupsData.groups?.length,
    };
    return (
      <NewLookGroupsTabs
        items={tabsItems.map((item) => ({ ...item, count: counts[item.key] }))}
      />
    );
  }

  return (
    <div
      style={{
        padding:
          IS_WHOP && isBottomNavigator
            ? 0
            : isNewLookPage
            ? `8px ${NEW_LOOK_PAGE_GUTTER_VAR} 24px`
            : `0 ${basePadding}px ${basePadding}px`,
        position: "relative",
      }}
    >
      {personalGroupsSection}
      {myGroupsSection}
      {!IS_WHOP && freeTrialGroupsSection}
    </div>
  );
};

const NEW_LOOK_HIDDEN_TITLE = { display: "none" };

interface NewLookGroupsTabsProps {
  items: (TabItem & { count?: number })[];
}

/** New look (desktop): one section at a time behind underline page tabs. */
const NewLookGroupsTabs = ({ items }: NewLookGroupsTabsProps) => {
  const [activeKey, setActiveKey] = useState(items[0]?.key);
  const active = items.find((item) => item.key === activeKey) ?? items[0];

  usePageIntro(
    {
      footer: (
        <NewLookIntroTabs
          activeKey={active?.key}
          onChange={setActiveKey}
          items={items.map((item) => ({
            key: item.key,
            label: (
              <TabLabel>
                {item.label}
                {item.count !== undefined && <TabCount>{item.count}</TabCount>}
              </TabLabel>
            ),
          }))}
        />
      ),
    },
    [
      active?.key,
      items.map((item) => `${item.key}:${item.count}:${item.label}`).join(),
    ]
  );

  return <NewLookContent>{active?.children as ReactNode}</NewLookContent>;
};

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;
const TabCount = styled.span`
  min-width: 20px;
  padding: 0 6px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) => p.theme.colors.cardLightBgColor};
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 11.5px;
  line-height: 18px;
  text-align: center;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
  font-variant-numeric: tabular-nums;
  .ant-tabs-tab-active & {
    background: ${(p) => p.theme.colors.lightBlue};
    color: ${(p) => p.theme.colors.primaryBlue};
  }
`;
const NewLookContent = styled.div`
  position: relative;
  padding: 4px ${NEW_LOOK_PAGE_GUTTER_VAR} 24px;
`;
